import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { BrandLogo, Button, PasswordInput, Stack } from '@fluent/ui';
import { supabase } from '../lib/supabase.js';
import { supabaseAuthErrorKey } from '../auth/errors.js';
import './auth.css';

/**
 * /reset-password — landing page for the link in Supabase's reset-password
 * email. Supabase automatically exchanges the `?code=...` on load (thanks
 * to `detectSessionInUrl: true` on the client), putting the user in a
 * short-lived password-recovery session. We then set the new password
 * via `updateUser`.
 */
export function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Wait until the session resolves — if a recovery session is present,
    // `getSession` returns a non-null user with no password yet.
    void supabase.auth.getSession().then(({ data }) => {
      if (!data.session) {
        // No valid recovery code → bounce back to forgot-password.
        navigate('/forgot-password', { replace: true });
        return;
      }
      setReady(true);
    });
  }, [navigate]);

  const submit = async (ev: FormEvent) => {
    ev.preventDefault();
    const e: typeof errors = {};
    if (!password) e.password = t('auth.errors.password_required');
    else if (password.length < 8) e.password = t('auth.errors.password_too_short');
    if (confirm !== password) e.confirm = t('auth.errors.password_mismatch');
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (error) {
      setErrors({ form: t(supabaseAuthErrorKey(error)) });
      return;
    }
    navigate('/', { replace: true });
  };

  if (!ready) return null;

  return (
    <main className="auth-screen">
      <form className="auth-card" onSubmit={submit} noValidate>
        <BrandLogo className="auth-card__brand" />
        <h1 className="auth-card__title">{t('auth.reset.title')}</h1>
        <p className="auth-card__subtitle">{t('auth.reset.subtitle')}</p>

        <Stack gap={4}>
          <PasswordInput
            label={t('auth.reset.password_label')}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={errors.password}
            required
          />
          <PasswordInput
            label={t('auth.reset.confirm_label')}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={errors.confirm}
            required
          />
          {errors.form && (
            <span role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--text-sm)', fontWeight: 700 }}>
              {errors.form}
            </span>
          )}
          <Button type="submit" variant="primary" size="lg" fullWidth loading={submitting}>
            {t('auth.reset.submit')}
          </Button>
        </Stack>
      </form>
    </main>
  );
}
